import React from 'react';

import { useStores } from '@src/Common/Hooks/useStores';

import DropdownAnomalyRating from '@src/Components/Atoms/DropdownAnomalyRating/DropdownAnomalyRating';
import DropdownEcosystem from '@src/Components/Atoms/DropdownEcosystem/DropdownEcosystem';
import DropdownYear from '@src/Components/Atoms/DropdownYear/DropdownYear';
import InputSearchEvent from '@src/Components/Atoms/InputSearchEvent/InputSearchEvent';
import { CloseIcon } from '@src/Components/Icons';
import DropdownCountry from '@src/Components/Molecules/DropdownCountry/DropdownCountry';
import DefaultModal from '@src/Components/Molecules/Modal/DefaultModal/DefaultModal';

import { debounce } from '@src/Common/Helpers/debounce';
import { observer } from 'mobx-react-lite';
import styles from './AllEventsDialog.module.scss';
import useAllEventsDialog from './useAllEventsDialog';
import AllEventList from '@src/Components/Molecules/AllEventList/AllEventList';

interface IAllEventsDialog {
  show: boolean;
  onClose: () => void;
}

const AllEventsDialog: React.FC<IAllEventsDialog> = ({ show, onClose }) => {
  const { AllEventStore } = useStores();
  useAllEventsDialog();
  const handleDebouncedInputChange = debounce((value: string) => {
    AllEventStore.handleSelectField('searchValue', value);
  }, 300);

  return (
    <>
      <DefaultModal
        show={show}
        isBackgroundContainers={true}
        backgroundContent="transparent"
      >
        <section data-testid="all-event-dialog" className={styles['Container']}>
          <header className={styles['Header']}>
            <h4>All Event</h4>
            <button onClick={onClose}>
              <CloseIcon height={14} width={14} />
            </button>
          </header>
          <main className={styles['Content']}>
            <div className={styles['Filter']}>
              <DropdownCountry
                disabled={AllEventStore.status.isLoading}
                defaultValue={AllEventStore.filters.contries}
                onChange={(e) => {
                  AllEventStore.handleSelectField('contries', e);
                }}
              />
              <DropdownYear
                disabled={AllEventStore.status.isLoading}
                defaultValue={AllEventStore.filters.year}
                onChange={(e) => {
                  AllEventStore.handleSelectField('year', e);
                }}
                canResetYear={true}
                list={AllEventStore.allYears}
              />
              <DropdownAnomalyRating
                disabled={AllEventStore.status.isLoading}
                defaultValue={
                  AllEventStore.filters.anomalyRating
                    ?.find((v) => v > 0)
                    ?.toString() ?? 'all'
                }
                onChange={(e) => {
                  if (e === 'all') {
                    AllEventStore.handleSelectField(
                      'anomalyRating',
                      Array.from({ length: 7 }, (_v, k) => k + 1)
                    );
                  } else {
                    AllEventStore.handleSelectField(
                      'anomalyRating',
                      Array.from({ length: 7 }, (_v, k) => {
                        return k === Number(e) - 1 ? Number(e) : -1;
                      })
                    );
                  }
                }}
              />
              <DropdownEcosystem
                disabled={AllEventStore.status.isLoading}
                defaultValue={
                  AllEventStore.filters.ecosystem
                    ?.find((v) => v > 0)
                    ?.toString() ?? 'all'
                }
                onChange={(e) => {
                  if (e === 'all') {
                    AllEventStore.handleSelectField(
                      'ecosystem',
                      Array.from({ length: 4 }, (_v, k) => k)
                    );
                  } else {
                    AllEventStore.handleSelectField(
                      'ecosystem',
                      Array.from({ length: 4 }, (_v, k) => {
                        return k === Number(e) ? Number(e) : -1;
                      })
                    );
                  }
                }}
              />
              <InputSearchEvent
                value={AllEventStore.filters.searchValue}
                onChange={(e) => {
                  handleDebouncedInputChange(
                    (e.target as HTMLTextAreaElement).value
                  );
                }}
                disabled={AllEventStore.status.isLoading}
                placeholder="Search Event"
              />
            </div>
            <AllEventList />
          </main>
        </section>
      </DefaultModal>
    </>
  );
};

export default observer(AllEventsDialog);
